import * as Types from "./types";

import { DocumentTypeDecoration } from "@graphql-typed-document-node/core";
import {
  useQuery,
  useInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
} from "@tanstack/react-query";
import { fetcher } from "../modules/shared/services/graphqlFetcher";

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<
    DocumentTypeDecoration<TResult, TVariables>["__apiType"]
  >;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const GetBlogsDocument = new TypedDocumentString(`
    query GetBlogs($first: Int!) {
  blogs(first: $first) {
    nodes {
      id
      handle
      title
    }
  }
}
    `);

export const useGetBlogsQuery = <TData = Types.GetBlogsQuery, TError = unknown>(
  variables: Types.GetBlogsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetBlogsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetBlogsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Types.GetBlogsQuery, TError, TData>({
    queryKey: ["GetBlogs", variables],
    queryFn: fetcher<Types.GetBlogsQuery, Types.GetBlogsQueryVariables>(
      GetBlogsDocument,
      variables,
    ),
    ...options,
  });
};

useGetBlogsQuery.getKey = (variables: Types.GetBlogsQueryVariables) => [
  "GetBlogs",
  variables,
];

export const useInfiniteGetBlogsQuery = <
  TData = InfiniteData<Types.GetBlogsQuery>,
  TError = unknown,
>(
  variables: Types.GetBlogsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetBlogsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetBlogsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetBlogsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetBlogs.infinite", variables],
        queryFn: (metaData) =>
          fetcher<Types.GetBlogsQuery, Types.GetBlogsQueryVariables>(
            GetBlogsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetBlogsQuery.getKey = (variables: Types.GetBlogsQueryVariables) => [
  "GetBlogs.infinite",
  variables,
];

useGetBlogsQuery.fetcher = (
  variables: Types.GetBlogsQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetBlogsQuery, Types.GetBlogsQueryVariables>(
    GetBlogsDocument,
    variables,
    options,
  );

export const GetBlogArticlesDocument = new TypedDocumentString(`
    query GetBlogArticles($blogHandle: String!, $first: Int!, $cursor: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  blog(handle: $blogHandle) {
    articles(first: $first, after: $cursor, sortKey: PUBLISHED_AT, reverse: true) {
      nodes {
        id
        handle
        title
        excerpt
        publishedAt
        image {
          url
          altText
          width
          height
        }
        authorV2 {
          name
        }
        blog {
          handle
          title
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
    `);

export const useGetBlogArticlesQuery = <
  TData = Types.GetBlogArticlesQuery,
  TError = unknown,
>(
  variables: Types.GetBlogArticlesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetBlogArticlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetBlogArticlesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetBlogArticlesQuery, TError, TData>({
    queryKey: ["GetBlogArticles", variables],
    queryFn: fetcher<
      Types.GetBlogArticlesQuery,
      Types.GetBlogArticlesQueryVariables
    >(GetBlogArticlesDocument, variables),
    ...options,
  });
};

useGetBlogArticlesQuery.getKey = (
  variables: Types.GetBlogArticlesQueryVariables,
) => ["GetBlogArticles", variables];

export const useInfiniteGetBlogArticlesQuery = <
  TData = InfiniteData<Types.GetBlogArticlesQuery>,
  TError = unknown,
>(
  variables: Types.GetBlogArticlesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetBlogArticlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetBlogArticlesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetBlogArticlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetBlogArticles.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetBlogArticlesQuery,
            Types.GetBlogArticlesQueryVariables
          >(GetBlogArticlesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetBlogArticlesQuery.getKey = (
  variables: Types.GetBlogArticlesQueryVariables,
) => ["GetBlogArticles.infinite", variables];

useGetBlogArticlesQuery.fetcher = (
  variables: Types.GetBlogArticlesQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetBlogArticlesQuery, Types.GetBlogArticlesQueryVariables>(
    GetBlogArticlesDocument,
    variables,
    options,
  );

export const GetArticleByHandleDocument = new TypedDocumentString(`
    query GetArticleByHandle($blogHandle: String!, $articleHandle: String!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  blog(handle: $blogHandle) {
    articleByHandle(handle: $articleHandle) {
      id
      handle
      title
      contentHtml
      excerpt
      publishedAt
      tags
      image {
        url
        altText
        width
        height
      }
      authorV2 {
        name
      }
      blog {
        handle
        title
      }
      seo {
        title
        description
      }
    }
  }
}
    `);

export const useGetArticleByHandleQuery = <
  TData = Types.GetArticleByHandleQuery,
  TError = unknown,
>(
  variables: Types.GetArticleByHandleQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetArticleByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetArticleByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetArticleByHandleQuery, TError, TData>({
    queryKey: ["GetArticleByHandle", variables],
    queryFn: fetcher<
      Types.GetArticleByHandleQuery,
      Types.GetArticleByHandleQueryVariables
    >(GetArticleByHandleDocument, variables),
    ...options,
  });
};

useGetArticleByHandleQuery.getKey = (
  variables: Types.GetArticleByHandleQueryVariables,
) => ["GetArticleByHandle", variables];

export const useInfiniteGetArticleByHandleQuery = <
  TData = InfiniteData<Types.GetArticleByHandleQuery>,
  TError = unknown,
>(
  variables: Types.GetArticleByHandleQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetArticleByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetArticleByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetArticleByHandleQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetArticleByHandle.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetArticleByHandleQuery,
            Types.GetArticleByHandleQueryVariables
          >(GetArticleByHandleDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetArticleByHandleQuery.getKey = (
  variables: Types.GetArticleByHandleQueryVariables,
) => ["GetArticleByHandle.infinite", variables];

useGetArticleByHandleQuery.fetcher = (
  variables: Types.GetArticleByHandleQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<
    Types.GetArticleByHandleQuery,
    Types.GetArticleByHandleQueryVariables
  >(GetArticleByHandleDocument, variables, options);

export const GetAllArticlesDocument = new TypedDocumentString(`
    query GetAllArticles($first: Int!, $query: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  articles(first: $first, sortKey: PUBLISHED_AT, reverse: true, query: $query) {
    nodes {
      id
      handle
      title
      excerpt
      publishedAt
      image {
        url
        altText
        width
        height
      }
      authorV2 {
        name
      }
      blog {
        handle
        title
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `);

export const useGetAllArticlesQuery = <
  TData = Types.GetAllArticlesQuery,
  TError = unknown,
>(
  variables: Types.GetAllArticlesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetAllArticlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetAllArticlesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetAllArticlesQuery, TError, TData>({
    queryKey: ["GetAllArticles", variables],
    queryFn: fetcher<
      Types.GetAllArticlesQuery,
      Types.GetAllArticlesQueryVariables
    >(GetAllArticlesDocument, variables),
    ...options,
  });
};

useGetAllArticlesQuery.getKey = (
  variables: Types.GetAllArticlesQueryVariables,
) => ["GetAllArticles", variables];

export const useInfiniteGetAllArticlesQuery = <
  TData = InfiniteData<Types.GetAllArticlesQuery>,
  TError = unknown,
>(
  variables: Types.GetAllArticlesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetAllArticlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetAllArticlesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetAllArticlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAllArticles.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetAllArticlesQuery,
            Types.GetAllArticlesQueryVariables
          >(GetAllArticlesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetAllArticlesQuery.getKey = (
  variables: Types.GetAllArticlesQueryVariables,
) => ["GetAllArticles.infinite", variables];

useGetAllArticlesQuery.fetcher = (
  variables: Types.GetAllArticlesQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetAllArticlesQuery, Types.GetAllArticlesQueryVariables>(
    GetAllArticlesDocument,
    variables,
    options,
  );

export const GetAvailableCountriesDocument = new TypedDocumentString(`
    query GetAvailableCountries {
  localization {
    availableCountries {
      isoCode
      name
    }
  }
}
    `);

export const useGetAvailableCountriesQuery = <
  TData = Types.GetAvailableCountriesQuery,
  TError = unknown,
>(
  variables?: Types.GetAvailableCountriesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetAvailableCountriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetAvailableCountriesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetAvailableCountriesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetAvailableCountries"]
        : ["GetAvailableCountries", variables],
    queryFn: fetcher<
      Types.GetAvailableCountriesQuery,
      Types.GetAvailableCountriesQueryVariables
    >(GetAvailableCountriesDocument, variables),
    ...options,
  });
};

useGetAvailableCountriesQuery.getKey = (
  variables?: Types.GetAvailableCountriesQueryVariables,
) =>
  variables === undefined
    ? ["GetAvailableCountries"]
    : ["GetAvailableCountries", variables];

export const useInfiniteGetAvailableCountriesQuery = <
  TData = InfiniteData<Types.GetAvailableCountriesQuery>,
  TError = unknown,
>(
  variables: Types.GetAvailableCountriesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetAvailableCountriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetAvailableCountriesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetAvailableCountriesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetAvailableCountries.infinite"]
            : ["GetAvailableCountries.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetAvailableCountriesQuery,
            Types.GetAvailableCountriesQueryVariables
          >(GetAvailableCountriesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetAvailableCountriesQuery.getKey = (
  variables?: Types.GetAvailableCountriesQueryVariables,
) =>
  variables === undefined
    ? ["GetAvailableCountries.infinite"]
    : ["GetAvailableCountries.infinite", variables];

useGetAvailableCountriesQuery.fetcher = (
  variables?: Types.GetAvailableCountriesQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<
    Types.GetAvailableCountriesQuery,
    Types.GetAvailableCountriesQueryVariables
  >(GetAvailableCountriesDocument, variables, options);

export const GetPageByHandleDocument = new TypedDocumentString(`
    query GetPageByHandle($handle: String!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  page(handle: $handle) {
    id
    title
    handle
    body
    bodySummary
    updatedAt
    seo {
      title
      description
    }
  }
}
    `);

export const useGetPageByHandleQuery = <
  TData = Types.GetPageByHandleQuery,
  TError = unknown,
>(
  variables: Types.GetPageByHandleQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPageByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPageByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetPageByHandleQuery, TError, TData>({
    queryKey: ["GetPageByHandle", variables],
    queryFn: fetcher<
      Types.GetPageByHandleQuery,
      Types.GetPageByHandleQueryVariables
    >(GetPageByHandleDocument, variables),
    ...options,
  });
};

useGetPageByHandleQuery.getKey = (
  variables: Types.GetPageByHandleQueryVariables,
) => ["GetPageByHandle", variables];

export const useInfiniteGetPageByHandleQuery = <
  TData = InfiniteData<Types.GetPageByHandleQuery>,
  TError = unknown,
>(
  variables: Types.GetPageByHandleQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPageByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPageByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetPageByHandleQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetPageByHandle.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetPageByHandleQuery,
            Types.GetPageByHandleQueryVariables
          >(GetPageByHandleDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetPageByHandleQuery.getKey = (
  variables: Types.GetPageByHandleQueryVariables,
) => ["GetPageByHandle.infinite", variables];

useGetPageByHandleQuery.fetcher = (
  variables: Types.GetPageByHandleQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetPageByHandleQuery, Types.GetPageByHandleQueryVariables>(
    GetPageByHandleDocument,
    variables,
    options,
  );

export const GetShopPoliciesDocument = new TypedDocumentString(`
    query GetShopPolicies($country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  shop {
    privacyPolicy {
      id
      title
      handle
      body
      url
    }
    refundPolicy {
      id
      title
      handle
      body
      url
    }
    shippingPolicy {
      id
      title
      handle
      body
      url
    }
    termsOfService {
      id
      title
      handle
      body
      url
    }
    contactInformation {
      id
      title
      handle
      body
      url
    }
    legalNotice {
      id
      title
      handle
      body
      url
    }
  }
}
    `);

export const useGetShopPoliciesQuery = <
  TData = Types.GetShopPoliciesQuery,
  TError = unknown,
>(
  variables?: Types.GetShopPoliciesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetShopPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetShopPoliciesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetShopPoliciesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetShopPolicies"]
        : ["GetShopPolicies", variables],
    queryFn: fetcher<
      Types.GetShopPoliciesQuery,
      Types.GetShopPoliciesQueryVariables
    >(GetShopPoliciesDocument, variables),
    ...options,
  });
};

useGetShopPoliciesQuery.getKey = (
  variables?: Types.GetShopPoliciesQueryVariables,
) =>
  variables === undefined
    ? ["GetShopPolicies"]
    : ["GetShopPolicies", variables];

export const useInfiniteGetShopPoliciesQuery = <
  TData = InfiniteData<Types.GetShopPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.GetShopPoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetShopPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetShopPoliciesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetShopPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetShopPolicies.infinite"]
            : ["GetShopPolicies.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetShopPoliciesQuery,
            Types.GetShopPoliciesQueryVariables
          >(GetShopPoliciesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetShopPoliciesQuery.getKey = (
  variables?: Types.GetShopPoliciesQueryVariables,
) =>
  variables === undefined
    ? ["GetShopPolicies.infinite"]
    : ["GetShopPolicies.infinite", variables];

useGetShopPoliciesQuery.fetcher = (
  variables?: Types.GetShopPoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetShopPoliciesQuery, Types.GetShopPoliciesQueryVariables>(
    GetShopPoliciesDocument,
    variables,
    options,
  );

export const GetProductByHandleDocument = new TypedDocumentString(`
    query GetProductByHandle($handle: String!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  product(handle: $handle) {
    id
    title
    description
    descriptionHtml
    productType
    tags
    vendor
    handle
    availableForSale
    category {
      id
    }
    featuredImage {
      id
      height
      width
      url
      altText
    }
    seo {
      description
      title
    }
    options {
      name
      values
    }
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    images(first: 20) {
      nodes {
        id
        url
        altText
        width
        height
      }
    }
    variants(first: 50) {
      nodes {
        id
        title
        sku
        product {
          title
        }
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
        quantityAvailable
        availableForSale
        basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
          value
        }
        selectedOptions {
          name
          value
        }
        image {
          id
          altText
          url
          width
          height
        }
      }
    }
  }
}
    `);

export const useGetProductByHandleQuery = <
  TData = Types.GetProductByHandleQuery,
  TError = unknown,
>(
  variables: Types.GetProductByHandleQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetProductByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetProductByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetProductByHandleQuery, TError, TData>({
    queryKey: ["GetProductByHandle", variables],
    queryFn: fetcher<
      Types.GetProductByHandleQuery,
      Types.GetProductByHandleQueryVariables
    >(GetProductByHandleDocument, variables),
    ...options,
  });
};

useGetProductByHandleQuery.getKey = (
  variables: Types.GetProductByHandleQueryVariables,
) => ["GetProductByHandle", variables];

export const useInfiniteGetProductByHandleQuery = <
  TData = InfiniteData<Types.GetProductByHandleQuery>,
  TError = unknown,
>(
  variables: Types.GetProductByHandleQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetProductByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetProductByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetProductByHandleQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetProductByHandle.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetProductByHandleQuery,
            Types.GetProductByHandleQueryVariables
          >(GetProductByHandleDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetProductByHandleQuery.getKey = (
  variables: Types.GetProductByHandleQueryVariables,
) => ["GetProductByHandle.infinite", variables];

useGetProductByHandleQuery.fetcher = (
  variables: Types.GetProductByHandleQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<
    Types.GetProductByHandleQuery,
    Types.GetProductByHandleQueryVariables
  >(GetProductByHandleDocument, variables, options);

export const GetProductByIdDocument = new TypedDocumentString(`
    query GetProductById($id: ID!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  product(id: $id) {
    id
    title
    availableForSale
    variants(first: 1) {
      nodes {
        id
        availableForSale
        price {
          amount
          currencyCode
        }
        basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
          value
        }
      }
    }
  }
}
    `);

export const useGetProductByIdQuery = <
  TData = Types.GetProductByIdQuery,
  TError = unknown,
>(
  variables: Types.GetProductByIdQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetProductByIdQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetProductByIdQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetProductByIdQuery, TError, TData>({
    queryKey: ["GetProductById", variables],
    queryFn: fetcher<
      Types.GetProductByIdQuery,
      Types.GetProductByIdQueryVariables
    >(GetProductByIdDocument, variables),
    ...options,
  });
};

useGetProductByIdQuery.getKey = (
  variables: Types.GetProductByIdQueryVariables,
) => ["GetProductById", variables];

export const useInfiniteGetProductByIdQuery = <
  TData = InfiniteData<Types.GetProductByIdQuery>,
  TError = unknown,
>(
  variables: Types.GetProductByIdQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetProductByIdQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetProductByIdQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetProductByIdQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetProductById.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetProductByIdQuery,
            Types.GetProductByIdQueryVariables
          >(GetProductByIdDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetProductByIdQuery.getKey = (
  variables: Types.GetProductByIdQueryVariables,
) => ["GetProductById.infinite", variables];

useGetProductByIdQuery.fetcher = (
  variables: Types.GetProductByIdQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetProductByIdQuery, Types.GetProductByIdQueryVariables>(
    GetProductByIdDocument,
    variables,
    options,
  );

export const GetProductRecommendationsDocument = new TypedDocumentString(`
    query GetProductRecommendations($productId: ID!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  productRecommendations(productId: $productId) {
    id
    title
    handle
    availableForSale
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    basePriceVariants: variants(first: 50) {
      nodes {
        price {
          amount
        }
        basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
          value
        }
      }
    }
    featuredImage {
      id
      altText
      width
      height
      url
    }
    images(first: 1) {
      nodes {
        id
        altText
        url
        height
        width
      }
    }
  }
}
    `);

export const useGetProductRecommendationsQuery = <
  TData = Types.GetProductRecommendationsQuery,
  TError = unknown,
>(
  variables: Types.GetProductRecommendationsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetProductRecommendationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetProductRecommendationsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetProductRecommendationsQuery, TError, TData>({
    queryKey: ["GetProductRecommendations", variables],
    queryFn: fetcher<
      Types.GetProductRecommendationsQuery,
      Types.GetProductRecommendationsQueryVariables
    >(GetProductRecommendationsDocument, variables),
    ...options,
  });
};

useGetProductRecommendationsQuery.getKey = (
  variables: Types.GetProductRecommendationsQueryVariables,
) => ["GetProductRecommendations", variables];

export const useInfiniteGetProductRecommendationsQuery = <
  TData = InfiniteData<Types.GetProductRecommendationsQuery>,
  TError = unknown,
>(
  variables: Types.GetProductRecommendationsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.GetProductRecommendationsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetProductRecommendationsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetProductRecommendationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetProductRecommendations.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          fetcher<
            Types.GetProductRecommendationsQuery,
            Types.GetProductRecommendationsQueryVariables
          >(GetProductRecommendationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetProductRecommendationsQuery.getKey = (
  variables: Types.GetProductRecommendationsQueryVariables,
) => ["GetProductRecommendations.infinite", variables];

useGetProductRecommendationsQuery.fetcher = (
  variables: Types.GetProductRecommendationsQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<
    Types.GetProductRecommendationsQuery,
    Types.GetProductRecommendationsQueryVariables
  >(GetProductRecommendationsDocument, variables, options);

export const SearchProductsDocument = new TypedDocumentString(`
    query SearchProducts($first: Int!, $query: String!, $cursor: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  search(
    after: $cursor
    first: $first
    query: $query
    prefix: NONE
    types: PRODUCT
  ) {
    pageInfo {
      endCursor
      hasNextPage
    }
    nodes {
      ... on Product {
        id
        title
        availableForSale
        productType
        handle
        basePriceVariants: variants(first: 50) {
          nodes {
            price {
              amount
            }
            basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
              value
            }
          }
        }
        featuredImage {
          id
          altText
          width
          height
          url
        }
        priceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
      }
    }
  }
}
    `);

export const useSearchProductsQuery = <
  TData = Types.SearchProductsQuery,
  TError = unknown,
>(
  variables: Types.SearchProductsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SearchProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SearchProductsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.SearchProductsQuery, TError, TData>({
    queryKey: ["SearchProducts", variables],
    queryFn: fetcher<
      Types.SearchProductsQuery,
      Types.SearchProductsQueryVariables
    >(SearchProductsDocument, variables),
    ...options,
  });
};

useSearchProductsQuery.getKey = (
  variables: Types.SearchProductsQueryVariables,
) => ["SearchProducts", variables];

export const useInfiniteSearchProductsQuery = <
  TData = InfiniteData<Types.SearchProductsQuery>,
  TError = unknown,
>(
  variables: Types.SearchProductsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SearchProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SearchProductsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.SearchProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["SearchProducts.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.SearchProductsQuery,
            Types.SearchProductsQueryVariables
          >(SearchProductsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteSearchProductsQuery.getKey = (
  variables: Types.SearchProductsQueryVariables,
) => ["SearchProducts.infinite", variables];

useSearchProductsQuery.fetcher = (
  variables: Types.SearchProductsQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.SearchProductsQuery, Types.SearchProductsQueryVariables>(
    SearchProductsDocument,
    variables,
    options,
  );

export const GetProductsDocument = new TypedDocumentString(`
    query GetProducts($first: Int!, $query: String, $cursor: String, $country: CountryCode, $language: LanguageCode, $sortKey: ProductSortKeys, $reverse: Boolean) @inContext(country: $country, language: $language) {
  products(
    first: $first
    query: $query
    after: $cursor
    sortKey: $sortKey
    reverse: $reverse
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    nodes {
      id
      title
      vendor
      description
      availableForSale
      productType
      handle
      basePriceVariants: variants(first: 50) {
        nodes {
          price {
            amount
          }
          basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
            value
          }
        }
      }
      featuredImage {
        id
        altText
        width
        height
        url
      }
      options {
        name
        values
      }
      colorMetafield: metafield(namespace: "shopify", key: "color-pattern") {
        value
        type
      }
      priceRange {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 1) {
        nodes {
          id
          image {
            url
            altText
            width
            height
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          selectedOptions {
            name
            value
          }
          product {
            handle
            title
          }
        }
      }
    }
  }
}
    `);

export const useGetProductsQuery = <
  TData = Types.GetProductsQuery,
  TError = unknown,
>(
  variables: Types.GetProductsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetProductsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetProductsQuery, TError, TData>({
    queryKey: ["GetProducts", variables],
    queryFn: fetcher<Types.GetProductsQuery, Types.GetProductsQueryVariables>(
      GetProductsDocument,
      variables,
    ),
    ...options,
  });
};

useGetProductsQuery.getKey = (variables: Types.GetProductsQueryVariables) => [
  "GetProducts",
  variables,
];

export const useInfiniteGetProductsQuery = <
  TData = InfiniteData<Types.GetProductsQuery>,
  TError = unknown,
>(
  variables: Types.GetProductsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetProductsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetProducts.infinite", variables],
        queryFn: (metaData) =>
          fetcher<Types.GetProductsQuery, Types.GetProductsQueryVariables>(
            GetProductsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetProductsQuery.getKey = (
  variables: Types.GetProductsQueryVariables,
) => ["GetProducts.infinite", variables];

useGetProductsQuery.fetcher = (
  variables: Types.GetProductsQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetProductsQuery, Types.GetProductsQueryVariables>(
    GetProductsDocument,
    variables,
    options,
  );

export const GetProductTypesDocument = new TypedDocumentString(`
    query GetProductTypes($first: Int!) {
  productTypes(first: $first) {
    edges {
      node
    }
  }
}
    `);

export const useGetProductTypesQuery = <
  TData = Types.GetProductTypesQuery,
  TError = unknown,
>(
  variables: Types.GetProductTypesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetProductTypesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetProductTypesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetProductTypesQuery, TError, TData>({
    queryKey: ["GetProductTypes", variables],
    queryFn: fetcher<
      Types.GetProductTypesQuery,
      Types.GetProductTypesQueryVariables
    >(GetProductTypesDocument, variables),
    ...options,
  });
};

useGetProductTypesQuery.getKey = (
  variables: Types.GetProductTypesQueryVariables,
) => ["GetProductTypes", variables];

export const useInfiniteGetProductTypesQuery = <
  TData = InfiniteData<Types.GetProductTypesQuery>,
  TError = unknown,
>(
  variables: Types.GetProductTypesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetProductTypesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetProductTypesQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetProductTypesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetProductTypes.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetProductTypesQuery,
            Types.GetProductTypesQueryVariables
          >(GetProductTypesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetProductTypesQuery.getKey = (
  variables: Types.GetProductTypesQueryVariables,
) => ["GetProductTypes.infinite", variables];

useGetProductTypesQuery.fetcher = (
  variables: Types.GetProductTypesQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetProductTypesQuery, Types.GetProductTypesQueryVariables>(
    GetProductTypesDocument,
    variables,
    options,
  );

export const GetCollectionsDocument = new TypedDocumentString(`
    query GetCollections($first: Int!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  collections(first: $first) {
    nodes {
      id
      handle
      title
      description
      image {
        url
        altText
      }
    }
  }
}
    `);

export const useGetCollectionsQuery = <
  TData = Types.GetCollectionsQuery,
  TError = unknown,
>(
  variables: Types.GetCollectionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetCollectionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetCollectionsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetCollectionsQuery, TError, TData>({
    queryKey: ["GetCollections", variables],
    queryFn: fetcher<
      Types.GetCollectionsQuery,
      Types.GetCollectionsQueryVariables
    >(GetCollectionsDocument, variables),
    ...options,
  });
};

useGetCollectionsQuery.getKey = (
  variables: Types.GetCollectionsQueryVariables,
) => ["GetCollections", variables];

export const useInfiniteGetCollectionsQuery = <
  TData = InfiniteData<Types.GetCollectionsQuery>,
  TError = unknown,
>(
  variables: Types.GetCollectionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetCollectionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetCollectionsQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetCollectionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetCollections.infinite", variables],
        queryFn: (metaData) =>
          fetcher<
            Types.GetCollectionsQuery,
            Types.GetCollectionsQueryVariables
          >(GetCollectionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetCollectionsQuery.getKey = (
  variables: Types.GetCollectionsQueryVariables,
) => ["GetCollections.infinite", variables];

useGetCollectionsQuery.fetcher = (
  variables: Types.GetCollectionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<Types.GetCollectionsQuery, Types.GetCollectionsQueryVariables>(
    GetCollectionsDocument,
    variables,
    options,
  );

export const GetCollectionByHandleDocument = new TypedDocumentString(`
    query GetCollectionByHandle($handle: String!, $numberOfProducts: Int!, $cursor: String, $country: CountryCode, $language: LanguageCode, $productFilters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean) @inContext(country: $country, language: $language) {
  collection(handle: $handle) {
    id
    handle
    title
    description
    image {
      url
      altText
    }
    seo {
      title
      description
    }
    products(
      first: $numberOfProducts
      filters: $productFilters
      after: $cursor
      sortKey: $sortKey
      reverse: $reverse
    ) {
      filters {
        id
        label
        type
        values {
          id
          label
          count
          input
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        title
        handle
        vendor
        description
        availableForSale
        productType
        options {
          name
          values
        }
        colorMetafield: metafield(namespace: "shopify", key: "color-pattern") {
          value
          type
        }
        variants(first: 20) {
          nodes {
            id
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            quantityAvailable
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
        basePriceVariants: variants(first: 50) {
          nodes {
            price {
              amount
            }
            basePrice: metafield(namespace: "custom", key: "sidrena_cijena") {
              value
            }
          }
        }
        featuredImage {
          id
          altText
          width
          height
          url
        }
        priceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 1) {
          nodes {
            id
            altText
            url
            height
            width
          }
        }
      }
    }
  }
}
    `);

export const useGetCollectionByHandleQuery = <
  TData = Types.GetCollectionByHandleQuery,
  TError = unknown,
>(
  variables: Types.GetCollectionByHandleQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetCollectionByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetCollectionByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useQuery<Types.GetCollectionByHandleQuery, TError, TData>({
    queryKey: ["GetCollectionByHandle", variables],
    queryFn: fetcher<
      Types.GetCollectionByHandleQuery,
      Types.GetCollectionByHandleQueryVariables
    >(GetCollectionByHandleDocument, variables),
    ...options,
  });
};

useGetCollectionByHandleQuery.getKey = (
  variables: Types.GetCollectionByHandleQueryVariables,
) => ["GetCollectionByHandle", variables];

export const useInfiniteGetCollectionByHandleQuery = <
  TData = InfiniteData<Types.GetCollectionByHandleQuery>,
  TError = unknown,
>(
  variables: Types.GetCollectionByHandleQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetCollectionByHandleQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetCollectionByHandleQuery,
      TError,
      TData
    >["queryKey"];
  },
) => {
  return useInfiniteQuery<Types.GetCollectionByHandleQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetCollectionByHandle.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          fetcher<
            Types.GetCollectionByHandleQuery,
            Types.GetCollectionByHandleQueryVariables
          >(GetCollectionByHandleDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      };
    })(),
  );
};

useInfiniteGetCollectionByHandleQuery.getKey = (
  variables: Types.GetCollectionByHandleQueryVariables,
) => ["GetCollectionByHandle.infinite", variables];

useGetCollectionByHandleQuery.fetcher = (
  variables: Types.GetCollectionByHandleQueryVariables,
  options?: RequestInit["headers"],
) =>
  fetcher<
    Types.GetCollectionByHandleQuery,
    Types.GetCollectionByHandleQueryVariables
  >(GetCollectionByHandleDocument, variables, options);
